import React, { useEffect, useState } from 'react';

import { Grid, Box } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';

import { CardComponent } from 'components/CardComponent/CardComponent';
import { DeleteCustomDomainDialog } from 'components/Dialogs/DeleteCustomDomainDialog/DeleteCustomDomainDialog';
import { TextValidatorComponent } from 'components/FormComponents';
import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { customDomainsModel } from 'models';
import { CustomDomain } from 'types/domains';

import { CollapsableDomain } from './CollapsableDomain/CollapsableDomain';

import { Description, Disclaimer, StyledButtonComponent } from './style';

interface CustomDomainsProps {
  clientId?: number;
  setDomains?: (domains: CustomDomain[]) => void;
}

export const CustomDomains = ({ clientId, setDomains }: CustomDomainsProps) => {
  const { t } = useTranslation();
  const { handleServerError } = useServerErrorActions();

  const [customDomainField, setCustomDomainField] = useState('');
  const [customDomains, setCustomDomains] = useState<CustomDomain[]>([]);
  const [domainToDelete, setDomainToDelete] = useState<CustomDomain>();

  useEffect(() => {
    getCustomDomains();
  }, []);

  const getCustomDomains = () =>
    customDomainsModel
      .getCustomDomains({ withDetails: true, manage: true }, clientId)
      .then(({ externalHosts }) => {
        setDomains?.(externalHosts);
        setCustomDomains(externalHosts);
      })
      .catch(handleServerError);

  const handleSubmit = () =>
    customDomainsModel
      .createCustomDomain(customDomainField, clientId)
      .then(() => {
        getCustomDomains();
        setCustomDomainField('');
      })
      .catch(handleServerError);

  const handleDelete = (id: number) =>
    customDomainsModel
      .deleteCustomDomain(id, clientId)
      .then(() => {
        getCustomDomains();
        setDomainToDelete(undefined);
      })
      .catch(handleServerError);

  const handleCheckStatus = (id: number) =>
    customDomainsModel.checkCustomDomainStatus(id, clientId).then(getCustomDomains).catch(handleServerError);

  return (
    <CardComponent title={t('components.customDomains.title')} sm={12}>
      <ValidatorForm onSubmit={handleSubmit} autoComplete="off">
        <Grid container spacing={6} direction="column">
          <Grid item>
            <Description>{t('components.customDomains.description')}</Description>
          </Grid>
          {customDomains.length > 0 && (
            <>
              <Grid item container direction="column">
                {customDomains.map(domain => (
                  <Grid item key={domain.host}>
                    <CollapsableDomain
                      domain={domain}
                      onDelete={() => setDomainToDelete(domain)}
                      onCheckStatus={handleCheckStatus}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid item>
                <Divider />
              </Grid>
            </>
          )}
          <Grid item container>
            <Box
              style={{
                flexGrow: 1,
              }}
            >
              <TextValidatorComponent
                validators={[VALIDATORS_MAP.isValidCustomDomain, VALIDATORS_MAP.required]}
                label={t('components.customDomains.fieldLabel')}
                value={customDomainField}
                fieldName="host"
                handleChange={({ host }: { host: string }) => setCustomDomainField(host)}
                disableMinHeight
              />
            </Box>
            <StyledButtonComponent
              color="primary"
              type="submit"
              text={t('components.customDomains.buttonLabel')}
              fullWidth
              disabled={!customDomainField}
            />
          </Grid>
          <Grid item>
            <Disclaimer>{t('components.customDomains.disclaimer')}</Disclaimer>
          </Grid>
        </Grid>
      </ValidatorForm>
      {domainToDelete && (
        <DeleteCustomDomainDialog
          onSubmit={() => handleDelete(domainToDelete.id)}
          onClose={() => setDomainToDelete(undefined)}
          domainName={domainToDelete.host}
        />
      )}
    </CardComponent>
  );
};
